import { useState } from 'react';

export function SellerFinancingHero() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyType: '',
    numberOfUnits: '',
    currentSituation: '',
    timeline: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qoadc98t1oshnre0d0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          propertyType: '',
          numberOfUnits: '',
          currentSituation: '',
          timeline: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center py-20 px-4 overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/39726e29c08798ace860b4307095cd05.jpg)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      
      <div className="relative z-10 w-full max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Seller Financing Benefits:<br />Tax Advantages &amp; Passive Income
          </h1>
          <p className="text-xl md:text-2xl text-white/95 max-w-4xl mx-auto leading-relaxed">
            Discover how seller financing can generate steady passive income, defer capital gains taxes, achieve higher sale prices, and expand your buyer pool when selling your apartment building
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8 md:p-10 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 text-center">
            Get Your Free Seller Financing Consultation
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Learn how seller financing can maximize your profits and minimize your taxes
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="seller-financing-consultation">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-semibold text-gray-700 mb-2">
                  When Are You Looking to Sell? *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select Timeline</option>
                  <option value="immediately">Immediately</option>
                  <option value="1-3-months">1-3 Months</option>
                  <option value="3-6-months">3-6 Months</option>
                  <option value="6-12-months">6-12 Months</option>
                  <option value="just-exploring">Just Exploring Options</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="propertyAddress" className="block text-sm font-semibold text-gray-700 mb-2">
                Property Address *
              </label>
              <input
                type="text"
                id="propertyAddress"
                name="propertyAddress"
                value={formData.propertyAddress}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="123 Main Street, City, State, ZIP"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="propertyType" className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Type *
                </label>
                <select
                  id="propertyType"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select Type</option>
                  <option value="duplex">Duplex (2 Units)</option>
                  <option value="triplex">Triplex (3 Units)</option>
                  <option value="fourplex">Fourplex (4 Units)</option>
                  <option value="small-apartment">Small Apartment (5-10 Units)</option>
                  <option value="medium-apartment">Medium Apartment (11-25 Units)</option>
                  <option value="large-apartment">Large Apartment (26+ Units)</option>
                  <option value="mixed-use">Mixed-Use Building</option>
                </select>
              </div>

              <div>
                <label htmlFor="numberOfUnits" className="block text-sm font-semibold text-gray-700 mb-2">
                  Number of Units *
                </label>
                <input
                  type="number"
                  id="numberOfUnits"
                  name="numberOfUnits"
                  value={formData.numberOfUnits}
                  onChange={handleChange}
                  required
                  min="2"
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="e.g., 8"
                />
              </div>
            </div>

            <div>
              <label htmlFor="currentSituation" className="block text-sm font-semibold text-gray-700 mb-2">
                Current Property Situation *
              </label>
              <select
                id="currentSituation"
                name="currentSituation"
                value={formData.currentSituation}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">Select Situation</option>
                <option value="fully-occupied">Fully Occupied</option>
                <option value="partially-occupied">Partially Occupied</option>
                <option value="vacant">Vacant</option>
                <option value="needs-repairs">Needs Repairs</option>
                <option value="good-condition">Good Condition</option>
                <option value="tenant-issues">Tenant Issues</option>
                <option value="financial-distress">Financial Distress</option>
              </select>
            </div>

            <div>
              <label htmlFor="additionalInfo" className="block text-sm font-semibold text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                placeholder="Tell us more about your property and seller financing goals..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.additionalInfo.length}/500 characters
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
                Thank you! We'll contact you shortly to discuss seller financing options.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
                Something went wrong. Please try again or call us directly.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-4 px-8 rounded-md transition-colors duration-200 text-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
            </button>

            <p className="text-xs text-gray-500 text-center">
              By submitting this form, you agree to be contacted about your property. We respect your privacy.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}